import React, { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  PersonSearchResultExternalPersonScopeEnum as PersonScopeEnum,
  PersonSearchResultExternal,
} from '@askporter/client-grieg-lyric';
import { AvatarSize, UserAvatar, BaseAutocomplete, RevampedIcon, RevampedIconSize } from '@askporter/component-library';
import { queryKeys } from '@askporter/query-keys';
import { ManagerRoles, useDebounce, fullName } from '@askporter/utils';
import doPeopleSearch from '../../PeopleSelector/utils/doPeopleSearch';

export interface PeopleSelectorProps {
  idPrefix: string;
  label: string;
  disabled?: boolean;
  selectedUser: PersonSearchResultExternal;
  onChange: (person: PersonSearchResultExternal) => void;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  textFieldVariant?: 'outlined' | 'filled';
  t: (key: string, options?: Record<string, string | number>) => string;
  textOnlyWhenOutlined?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  readOnlyText?: React.ReactNode;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  inlineLabel?: boolean;
}

export const RevampedPeopleAutocomplete = ({
  idPrefix,
  label,
  disabled,
  selectedUser,
  onChange,
  onBlur = () => {},
  t,
  placeholder,
  readOnly = false,
  readOnlyText = undefined,
  fullWidth,
  size = 'small',
  inlineLabel,
}: PeopleSelectorProps): JSX.Element => {
  const [user, setUser] = useState<PersonSearchResultExternal>(selectedUser);
  const [inputValue, setInputValue] = useState('');
  const userKeyword = useDebounce<string>(inputValue, 700);

  React.useEffect(() => {
    setUser(selectedUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedUser)]);

  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, status } = useInfiniteQuery({
    queryKey: queryKeys.people.people.search({ search: { freeText: userKeyword } }),
    queryFn: async ({ pageParam }) => {
      const response = await doPeopleSearch({
        search: {
          freeText: userKeyword,
          page: pageParam,
          filter: {
            scope: ['USERS', 'TEAMS'],
          },
        },
      });
      return { response, nextPage: pageParam + 1 };
    },
    initialPageParam: 1,
    enabled: userKeyword?.length > 2,
    getNextPageParam: (data) => {
      return data?.response?.summary?.totalPages >= data?.nextPage ? data?.nextPage : undefined;
    },
  });

  const getName = (user: PersonSearchResultExternal) => {
    if (user) {
      if (user?.personScope === PersonScopeEnum.User) {
        return fullName(user?.givenName, user?.familyName);
      }
      return user?.name;
    }
    return '';
  };

  const filterManagersTeams = (results: PersonSearchResultExternal[]) => {
    return results.filter(
      (entry) =>
        entry.personScope === PersonScopeEnum.Team ||
        (entry.personScope === PersonScopeEnum.User && ManagerRoles.includes(entry.roleSlug)),
    );
  };

  const opts =
    data?.pages && data?.pages.length > 0
      ? data.pages.reduce((acc, itm) => acc.concat(filterManagersTeams([...itm.response.results])), [])
      : [];

  return (
    <BaseAutocomplete
      t={t}
      label={label}
      idPrefix={idPrefix || 'people-selector'}
      disabled={disabled}
      size={size}
      options={[...opts]}
      value={user || null}
      placeholder={placeholder}
      onChange={(newValue: PersonSearchResultExternal) => onChange(newValue)}
      onInputChange={(_: React.SyntheticEvent, newInputValue: string) => {
        setInputValue(newInputValue);
      }}
      inputValue={inputValue}
      loading={status === 'pending' || isFetchingNextPage}
      onScrollToBottom={() => hasNextPage && fetchNextPage()}
      startAdornment={
        user && inputValue === getName(user) && user?.profilePhoto?.filePath ? (
          <>
            {user.personScope === PersonScopeEnum.User && (
              <UserAvatar
                givenName={user.givenName}
                familyName={user.familyName}
                imagePath={user?.profilePhoto?.filePath}
                avatarSize={AvatarSize.XS}
              />
            )}
            {user.personScope === PersonScopeEnum.Team && (
              <UserAvatar longName={user.name} imagePath={user?.profilePhoto?.filePath} avatarSize={AvatarSize.XS} />
            )}
          </>
        ) : (
          <RevampedIcon fullPath="/media/icons/design-system/user/20px.svg" iconSize={RevampedIconSize.SM} />
        )
      }
      mapOptionToValue={(option: PersonSearchResultExternal) => {
        return {
          uid: option.uid,
          label: getName(option),
          avatar: option?.profilePhoto?.filePath,
        };
      }}
      onBlur={(e) => onBlur(e)}
      fullWidth={fullWidth}
      inlineLabel={inlineLabel}
      {...{ readOnly, readOnlyText }}
    />
  );
};
